// Cycle reconciliation (show / hide): tips vs invoices check for the period.
// Part of the Balances page (components/Balances.jsx). Split out 2026-10-14.
import { fmtMoneyRounded as fmtMoney } from '../../utils/money.js';

export default function ReconPanel({ customers, from, loading, recon, reconBusy, reconOpen, setReconOpen, to, totals }) {
  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        <button className="refresh-btn" onClick={() => setReconOpen(o => !o)} disabled={loading}
                style={{ background: reconOpen ? '#475569' : '#0369a1' }}>
          {reconOpen ? '▼ Hide cycle reconciliation' : '▶ Show cycle reconciliation'}
        </button>
      </div>
      {reconOpen && (
        <div className="card" style={{ marginTop: '0.5rem', padding: '1rem' }}>
          {reconBusy && <div className="small" style={{ color: '#666' }}>Loading reconciliation…</div>}
          {recon && recon.error && <div className="small" style={{ color: '#991b1b' }}>Reconciliation failed: {recon.error}</div>}
          {recon && !recon.error && (
            <>
              <h3 style={{ marginTop: 0 }}>1) QB invoices vs cycle tip table</h3>
              <div className="card" style={{ padding: '0.5rem 0.75rem', background: '#f0f9ff', marginBottom: '0.75rem', fontSize: '0.9em' }}>
                <strong>Tips:</strong> {recon.invoiceRecon.totals.tipCount} loads @ {fmtMoney(recon.invoiceRecon.totals.tipValue)}
                {' · '}<strong>QB invoices:</strong> {recon.invoiceRecon.totals.invoiceCount} @ {fmtMoney(recon.invoiceRecon.totals.invoiceValue)}
                {' · '}<strong>Disposal (B+N):</strong> {fmtMoney(recon.invoiceRecon.totals.disposalFees)}
                {' · '}<strong>Other:</strong> {fmtMoney(recon.invoiceRecon.totals.otherInvoiced)}
                {' · '}<strong style={{ color: Math.abs(recon.invoiceRecon.totals.variance) > 1 ? '#dc2626' : '#16a34a' }}>
                  Variance (disposal − tips): {fmtMoney(recon.invoiceRecon.totals.variance)}</strong>
              </div>
              <details style={{ marginBottom: '0.75rem' }}>
                <summary className="small" style={{ cursor: 'pointer', color: '#0369a1' }}>QB line-item breakdown (all items billed in this cycle)</summary>
                <table className="fs-table tidy" style={{ fontSize: '0.85em', marginTop: '0.25rem' }}>
                  <thead><tr><th>Line item</th><th className="num">Total</th></tr></thead>
                  <tbody>
                    {recon.invoiceRecon.lineItemBreakdown.map(li => (
                      <tr key={li.item}><td>{li.item}</td><td className="num"><strong>{fmtMoney(li.total)}</strong></td></tr>
                    ))}
                  </tbody>
                </table>
              </details>
              <h4 style={{ marginTop: '1rem' }}>Per-customer reconciliation (largest variance first)</h4>
              <table className="fs-table tidy" style={{ fontSize: '0.85em' }}>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="num">Tips #</th><th className="num">Tips $</th>
                    <th className="num">QB inv #</th><th className="num">QB total $</th>
                    <th className="num">Disposal $</th><th className="num">Other $</th>
                    <th className="num">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {recon.invoiceRecon.customers.slice(0, 50).map(c => {
                    const varColor = Math.abs(c.variance) > 1 ? '#dc2626' : '#16a34a';
                    return (
                      <tr key={c.customer}>
                        <td>{c.customer}</td>
                        <td className="num">{c.tipCount}</td>
                        <td className="num">{fmtMoney(c.tipValue)}</td>
                        <td className="num">{c.invoiceCount}</td>
                        <td className="num">{fmtMoney(c.invoiceTotal)}</td>
                        <td className="num">{fmtMoney(c.disposal)}</td>
                        <td className="num">{fmtMoney(c.other)}</td>
                        <td className="num" style={{ color: varColor, fontWeight: Math.abs(c.variance) > 1 ? 600 : 400 }}>{fmtMoney(c.variance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <h3 style={{ marginTop: '1.5rem' }}>2) Sheet inbox vs database row counts</h3>
              <div className="card" style={{ padding: '0.5rem 0.75rem', background: '#f0f9ff', marginBottom: '0.75rem', fontSize: '0.9em' }}>
                <strong>Sheet rows in cycle:</strong> {recon.rowRecon.sheetCount}
                {' · '}<strong>Approved rows:</strong> {recon.rowRecon.excelCount}
                {' · '}<strong>Sheet pending:</strong> {recon.rowRecon.googlePending}
                {' · '}<strong>DB coverage (unique date+time):</strong> {recon.rowRecon.dbCoverageKeys}
                {' · '}
                {recon.rowRecon.isBalanced
                  ? <strong style={{ color: '#16a34a' }}>✓ Sheet and DB are balanced</strong>
                  : <strong style={{ color: '#dc2626' }}>✗ Variance: {recon.rowRecon.sheetOnlyCount} sheet-only, {recon.rowRecon.dbOnlyCount} DB-only</strong>}
              </div>
              {recon.rowRecon.sheetOnlyCount > 0 && (
                <details style={{ marginBottom: '0.5rem' }}>
                  <summary className="small" style={{ cursor: 'pointer', color: '#991b1b' }}>
                    {recon.rowRecon.sheetOnlyCount} sheet row(s) MISSING from database (first 20 shown)
                  </summary>
                  <table className="fs-table tidy" style={{ fontSize: '0.85em', marginTop: '0.25rem' }}>
                    <thead><tr><th>Date</th><th>Time</th><th>Plate</th><th>Customer</th></tr></thead>
                    <tbody>
                      {recon.rowRecon.sheetOnlyPreview.map((r, i) => (
                        <tr key={i}><td>{r.date}</td><td>{r.time}</td><td>{r.plate}</td><td>{r.customer}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="small" style={{ color: '#666', marginTop: '0.25rem' }}>
                    Fix: click <strong>Full re-sync</strong> on the Review tab to pull these into the database.
                  </div>
                </details>
              )}
              {recon.rowRecon.dbOnlyCount > 0 && (
                <details style={{ marginBottom: '0.5rem' }}>
                  <summary className="small" style={{ cursor: 'pointer', color: '#854d0e' }}>
                    {recon.rowRecon.dbOnlyCount} database row(s) NOT in sheet (first 20 shown)
                  </summary>
                  <table className="fs-table tidy" style={{ fontSize: '0.85em', marginTop: '0.25rem' }}>
                    <thead><tr><th>Date</th><th>Time</th><th>Plate</th><th>Customer</th><th>Source</th></tr></thead>
                    <tbody>
                      {recon.rowRecon.dbOnlyPreview.map((r, i) => (
                        <tr key={i}><td>{r.date}</td><td>{r.time}</td><td>{r.plate}</td><td>{r.customer}</td><td>{r.source}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="small" style={{ color: '#666', marginTop: '0.25rem' }}>
                    These came from the historical seed or were edited in the database after import. Likely intentional but worth checking.
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
